/**
 * Firebase Configuration and Push Notification Service
 * Handles real push notifications via Firebase Cloud Messaging (FCM)
 * 
 * SETUP REQUIRED:
 * 1. Add Firebase credentials to frontend/.env
 * 2. Add Firebase Server Key to backend/.env
 */

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Check if Firebase is configured
export const isFirebaseConfigured = () => {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    firebaseConfig.messagingSenderId &&
    VAPID_KEY
  );
};

let messaging = null;
let app = null;

/**
 * Initialize Firebase (lazy loading)
 */
export const initializeFirebase = async () => {
  if (!isFirebaseConfigured()) {
    console.log('Firebase not configured - push notifications disabled');
    return null;
  }

  try {
    // Dynamic import for code splitting
    const { initializeApp } = await import('firebase/app');
    const { getMessaging, getToken, onMessage } = await import('firebase/messaging');

    if (!app) {
      app = initializeApp(firebaseConfig);
    }

    messaging = getMessaging(app);
    console.log('Firebase initialized successfully');
    return messaging;
  } catch (error) {
    console.error('Firebase initialization error:', error);
    return null;
  }
};

/**
 * Request notification permission and get FCM token
 * Returns the token if successful, null otherwise
 */
export const requestPushToken = async () => {
  if (!isFirebaseConfigured()) {
    console.log('Firebase not configured - cannot get push token');
    return null;
  }

  try {
    // Request notification permission
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return null;
    }

    // Initialize Firebase if not already done
    if (!messaging) {
      await initializeFirebase();
    }

    if (!messaging) {
      return null;
    }

    // Register the Firebase messaging service worker explicitly
    let swRegistration = null;
    if ('serviceWorker' in navigator) {
      try {
        swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
          scope: '/firebase-cloud-messaging-push-scope'
        });
        console.log('Firebase messaging SW registered:', swRegistration.scope);
      } catch (swError) {
        console.warn('Firebase messaging SW registration failed:', swError);
      }
    }

    const { getToken } = await import('firebase/messaging');
    
    // Get the FCM token with service worker registration
    const tokenOptions = {
      vapidKey: VAPID_KEY,
    };
    
    if (swRegistration) {
      tokenOptions.serviceWorkerRegistration = swRegistration;
    }
    
    const token = await getToken(messaging, tokenOptions);

    if (token) {
      console.log('FCM Token obtained:', token.substring(0, 20) + '...');
      return token;
    } else {
      console.log('No FCM token available');
      return null;
    }
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
};

/**
 * Register device token with backend
 */
export const registerDeviceToken = async (token) => {
  try {
    const authToken = localStorage.getItem('token');
    if (!authToken) {
      console.log('No auth token - cannot register device');
      return false;
    }

    const API = process.env.REACT_APP_BACKEND_URL + '/api';
    const response = await fetch(`${API}/devices/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        device_token: token,
        platform: 'web',
      }),
    });

    if (response.ok) {
      const data = await response.json();
      console.log('Device registered for push:', data.message);
      return true;
    } else {
      console.error('Failed to register device');
      return false;
    }
  } catch (error) {
    console.error('Error registering device:', error);
    return false;
  }
};

/**
 * Setup foreground message listener
 * This handles notifications when the app is in the foreground
 */
export const setupForegroundListener = async (onMessageCallback) => {
  if (!messaging) {
    await initializeFirebase();
  }

  if (!messaging) {
    return;
  }

  const { onMessage } = await import('firebase/messaging');
  
  onMessage(messaging, (payload) => {
    console.log('Foreground message received:', payload);
    
    // Show notification manually for foreground
    if (payload.notification) {
      const { title, body } = payload.notification;
      
      // Use browser notification API
      if (Notification.permission === 'granted') {
        new Notification(title, {
          body,
          icon: '/icon-192x192.png',
          badge: '/badge-72x72.png',
          vibrate: [200, 100, 200],
        });
      }
    }
    
    // Call custom callback if provided
    if (onMessageCallback) {
      onMessageCallback(payload);
    }
  });
};

/**
 * Initialize push notifications for the app
 * Call this when user logs in or on app startup
 */
export const initializePushNotifications = async () => {
  if (!isFirebaseConfigured()) {
    console.log('Push notifications: Firebase not configured');
    return { success: false, reason: 'Firebase not configured' };
  }

  try {
    // Get FCM token
    const token = await requestPushToken();
    
    if (!token) {
      return { success: false, reason: 'No token obtained' };
    }

    // Register with backend
    const registered = await registerDeviceToken(token);
    
    if (!registered) {
      return { success: false, reason: 'Backend registration failed' };
    }

    // Setup foreground listener
    await setupForegroundListener();

    return { success: true, token };
  } catch (error) {
    console.error('Push notification setup error:', error);
    return { success: false, reason: error.message };
  }
};

export default {
  isFirebaseConfigured,
  initializeFirebase,
  requestPushToken,
  registerDeviceToken,
  setupForegroundListener,
  initializePushNotifications,
};
